import { Injectable } from '@angular/core';
import { AngularFirestore } from '@angular/fire/firestore';
import { AngularFireAuth } from '@angular/fire/auth';
import * as firebase from 'firebase/app';
import { Observable, BehaviorSubject, from, combineLatest } from 'rxjs';
import { map, switchMap, tap, catchError, take } from 'rxjs/operators';
import { writeLog } from '../misc/logs';
import { QuickSMS, Contact } from '../model';
import { DateTime } from '../misc/dateutil';

// Declare global Firebase variables provided by the Canvas environment (used for AngularFire module init)
declare const __app_id: string;
declare const __firebase_config: string;
declare const __initial_auth_token: string;

// Constants for chunking recipients
const RECIPIENT_CHUNK_SIZE = 5000; // Number of contacts per chunk document
const RECIPIENT_CHUNK_PREFIX = '-recipients-chunk-'; // Suffix for chunk document IDs

@Injectable({
  providedIn: 'root'
})
export class FirestoreService {
  private userId: string | null = null;
  private isFirebaseReady = new BehaviorSubject<boolean>(false);

  constructor(
    private afs: AngularFirestore, // AngularFire Firestore service
    private afAuth: AngularFireAuth // AngularFire Auth service
  ) {
    this.initAuthAndFirestore();
  }

  private initAuthAndFirestore() {
    this.afAuth.authState.subscribe(user => {
      if (user) {
        this.userId = user.uid;
        this.isFirebaseReady.next(true);
        writeLog('CCC:: AngularFire: User authenticated and Firestore ready:', this.userId);
      } else {
        this.userId = null;
        this.isFirebaseReady.next(false);
        writeLog('CCC:: AngularFire: Auth state changed: No user.');
        // If no user, attempt anonymous sign-in using the provided token if available
        if (typeof __initial_auth_token !== 'undefined' && __initial_auth_token) {
          this.afAuth.signInWithCustomToken(__initial_auth_token).then(() => {
            writeLog('CCC:: AngularFire: Signed in with custom token.');
          }).catch(error => {
            writeLog('CCC:: AngularFire: Custom token sign-in failed:', error);
            // Fallback to anonymous if custom token fails or is not provided
            this.afAuth.signInAnonymously().then(() => {
              writeLog('CCC:: AngularFire: Signed in anonymously.');
            }).catch(anonError => {
              writeLog('CCC:: AngularFire: Anonymous sign-in failed:', anonError);
            });
          });
        } else {
          this.afAuth.signInAnonymously().then(() => {
            writeLog('CCC:: AngularFire: Signed in anonymously (no initial token).');
          }).catch(anonError => {
            writeLog('CCC:: AngularFire: Anonymous sign-in failed:', anonError);
          });
        }
      }
    });
  }


  /**
   * Helper to get an AngularFirestoreDocument reference or a Collection reference
   * using the global __app_id and current userId.
   * Ensures data is stored in the correct user-specific path.
   *
   * @param collectionName The name of the collection (e.g., 'messages').
   * @param docId Optional: The ID of the document (e.g., 'currentMessage'). If not provided, returns collection reference.
   * @returns An AngularFirestoreDocument reference, Collection reference, or null if user is not authenticated.
   */
  private getFirestorePath() {
    if (!this.userId) {
      writeLog('CCC:: FirestoreService: User ID not available, cannot get document reference.');
      return null;
    }
    const appId = typeof __app_id !== 'undefined' ? __app_id : 'default-app-id';
    return `artifacts/${appId}/users/${this.userId}/messageDetails`; // Base path for message-related documents
  }

  /**
   * Saves QuickSMS message details to Firestore, storing recipients in chunked arrays
   * across multiple documents within the same collection to avoid the 1MB document size limit.
   *
   * @param messageDetails The QuickSMS object to save.
   * @param docName The base name for the main message document (e.g., 'currentMessage').
   * @returns An Observable that completes when the save operation is done.
   */
  saveMessageDetails(messageDetails: QuickSMS, docName: string = 'currentMessage'): Observable<void> {
    return this.isFirebaseReady.pipe(
      switchMap(async (ready) => {
        if (!ready || !this.userId) {
          writeLog('CCC:: Firestore not ready or user not authenticated for saving.');
          throw new Error('Firestore not ready or user not authenticated.');
        }

        const basePath = this.getFirestorePath();
        if (!basePath) {
          throw new Error('Could not get base Firestore path.');
        }

        const mainDocRef = this.afs.doc<QuickSMS>(`${basePath}/${docName}`);

        // Extract recipients to be stored in chunked documents
        const recipients = messageDetails.recipients;
        const totalRecipientsCount = recipients.length;

        // Create a copy of messageDetails for the main document,
        // ensuring recipients array is empty and adding totalRecipientsCount
        const messageMetadata = {
          ...messageDetails,
          recipients: [], // Recipients are stored separately
          datetime: messageDetails.datetime,
          totalRecipientsCount: totalRecipientsCount, // Store total count
        };

        // 1. Save the main message metadata document
        await mainDocRef.set(messageMetadata);

        // 2. Delete existing recipient chunk documents for this message
        const collectionRef = this.afs.collection<any>(basePath).ref; // Get native Firestore CollectionReference
        const querySnapshot = await collectionRef
          .where(firebase.firestore.FieldPath.documentId(), '>=', `${docName}${RECIPIENT_CHUNK_PREFIX}0`)
          .where(firebase.firestore.FieldPath.documentId(), '<=', `${docName}${RECIPIENT_CHUNK_PREFIX}\uf8ff`) // Unicode character for "last string in range"
          .get();

        let deleteBatch = this.afs.firestore.batch();
        let deleteCount = 0;
        const BATCH_LIMIT = 400; // Safety margin for Firestore's 500 operations per batch

        querySnapshot.forEach(doc => {
          deleteBatch.delete(doc.ref);
          deleteCount++;
          if (deleteCount >= BATCH_LIMIT) {
            deleteBatch.commit();
            deleteBatch = this.afs.firestore.batch();
            deleteCount = 0;
          }
        });
        if (deleteCount > 0) {
          await deleteBatch.commit();
        }
        writeLog('CCC:: Existing recipient chunk documents deleted successfully.');

        // 3. Add new recipient chunk documents
        let addBatch = this.afs.firestore.batch();
        let addCount = 0;
        const numChunks = Math.ceil(recipients.length / RECIPIENT_CHUNK_SIZE);

        for (let i = 0; i < numChunks; i++) {
          const chunk = recipients.slice(i * RECIPIENT_CHUNK_SIZE, (i + 1) * RECIPIENT_CHUNK_SIZE);
          const chunkDocId = `${docName}${RECIPIENT_CHUNK_PREFIX}${i}`;
          const chunkDocRef = this.afs.doc(`${basePath}/${chunkDocId}`).ref; // Get native DocumentReference for batch

          addBatch.set(chunkDocRef, { contacts: chunk }); // Store the chunked array under a 'contacts' field
          addCount++;

          if (addCount >= BATCH_LIMIT) {
            await addBatch.commit();
            addCount = 0;
            addBatch = this.afs.firestore.batch();
          }
        }
        if (addCount > 0) {
          await addBatch.commit();
        }

        writeLog(`CCC:: Message details and ${totalRecipientsCount} recipients saved in ${numChunks} chunks successfully.`);
      }),
      tap(() => writeLog('CCC:: Final save operation completed.')),
      catchError(error => {
        writeLog('CCC:: Error saving message details and recipients:', error);
        return from(Promise.reject(error));
      })
    );
  }

  /**
   * Loads QuickSMS message details from Firestore, including recipients from its chunked documents.
   * @param docName The base name of the main message document (e.g., 'currentMessage').
   * @returns An Observable that emits the QuickSMS object or null if not found.
   */
  loadMessageDetails(docName: string = 'currentMessage'): Observable<QuickSMS | null> {
    return this.isFirebaseReady.pipe(
      switchMap(ready => {
        if (!ready || !this.userId) {
          writeLog('CCC:: Firestore not ready or user not authenticated for loading.');
          return from(Promise.resolve(null));
        }

        const basePath = this.getFirestorePath();
        if (!basePath) {
          return from(Promise.resolve(null));
        }

        const mainDocRef = this.afs.doc<QuickSMS>(`${basePath}/${docName}`);

        // Load main message metadata
        const messageMetadata$ = mainDocRef.valueChanges().pipe(take(1));

        // Query for recipient chunk documents
        const recipientChunks$ = this.afs.collection<any>(basePath, ref =>
          ref.where(firebase.firestore.FieldPath.documentId(), '>=', `${docName}${RECIPIENT_CHUNK_PREFIX}0`)
             .where(firebase.firestore.FieldPath.documentId(), '<=', `${docName}${RECIPIENT_CHUNK_PREFIX}\uf8ff`)
        ).valueChanges().pipe(take(1)); // Take(1) to get current values and complete

        // Combine both observables to get message metadata and its recipient chunks
        return combineLatest([messageMetadata$, recipientChunks$]).pipe(
          map(([metadata, chunkDocs]) => {
            if (metadata) {
              // Reconstruct DateTime object if it was serialized as string
              if (metadata.datetime && typeof metadata.datetime === 'string') {
                metadata.datetime = DateTime.fromDateTime(metadata.datetime);
              }

              // Reconstruct recipients array from chunks
              const reconstructedRecipients: Contact[] = [];
              if (chunkDocs && chunkDocs.length > 0) {
                // Ensure chunks are processed in order if possible (though query returns by ID)
                // For a robust solution, you might sort chunkDocs by ID if necessary
                chunkDocs.forEach(chunkDoc => {
                  if (chunkDoc.contacts && Array.isArray(chunkDoc.contacts)) {
                    reconstructedRecipients.push(...chunkDoc.contacts);
                  }
                });
              }

              metadata.recipients = reconstructedRecipients;
              writeLog('CCC:: Message details and recipients loaded from Firestore:', metadata);
              return metadata;
            } else {
              writeLog('CCC:: No message details found in Firestore.');
              return null;
            }
          }),
          catchError(error => {
            writeLog('CCC:: Error loading message details from Firestore:', error);
            return from(Promise.resolve(null));
          })
        );
      })
    );
  }
}
